import { AnalyticsContext } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { convertEventUrl, ensureHttpsPrefix, getIsAllowListedUrl } from './SafeLink.utils';
import { formatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';
import { LinkConfirmationDialog } from '@shared/components/SafeLink/LinkConfirmationDialog';
import { useWebsiteInterceptExternalHotelLinks } from './hooks/useWebsiteInterceptExternalHotelLinks';
import { HotelSuggestionDialog } from './HotelSuggestionDilaog/HotelSuggestionDialog';
import { withWindow } from '@shared/utils/withWindow';
import { useEventInfo } from '@shared/utils/eventInfo';

const wrapHtmlWithSafeLink = <P extends object & { id: string; text: string }>(WrappedComponent: React.FC<P>) => {
  return (props: P) => {
    const analytics = useContext(AnalyticsContext);
    const safeLinksEnabled = useFeatureValue('guestSiteSafeLinks');
    const guestLinkUseRedirectService = useFeatureValue('guestLinkUseRedirectService').value === 'on';
    const { eventHandle } = useEventInfo();

    const { getShowIntercept } = useWebsiteInterceptExternalHotelLinks();

    const [isOpen, setIsOpen] = useState<{ type: 'safeLinkConfirmation' | 'intercept'; isAllowListedUrl: boolean } | null>(null);
    const [url, setUrl] = useState('');

    useEffect(() => {
      const links = document.querySelectorAll(`.richtext-${props.id} a`);

      const handleClick = (event: Event) => {
        event.preventDefault();

        const link = event.currentTarget as HTMLAnchorElement;
        const href = link.getAttribute('href');

        if (href) {
          const isAllowListedUrl = getIsAllowListedUrl(href);
          const type = href.startsWith('mailto:') ? 'email' : 'url';
          analytics.track({
            action: 'LinkInteracted',
            category: 'safeLink',
            extraInfo: { actionType: 'click', url: href, isAllowListedUrl, source: href, type },
          });
          const showIntercept = getShowIntercept(href);
          if (showIntercept) {
            setIsOpen({ type: 'intercept', isAllowListedUrl });
            setUrl(ensureHttpsPrefix(href));
          } else if (isAllowListedUrl || !safeLinksEnabled) {
            const safeLink = guestLinkUseRedirectService ? formatRedirectURL({ url: ensureHttpsPrefix(href) }) : ensureHttpsPrefix(href);
            withWindow(global => {
              global.open(safeLink, '_blank');
            });
          } else {
            setUrl(ensureHttpsPrefix(href));
            setIsOpen({ type: 'safeLinkConfirmation', isAllowListedUrl });
          }
        }
      };

      links.forEach(link => {
        const href = link.getAttribute('href');
        if (href) {
          link.setAttribute('href', convertEventUrl(href, eventHandle));
        }
        link.addEventListener('click', handleClick);
      });

      return () => {
        links.forEach(link => {
          link.removeEventListener('click', handleClick);
        });
      };
    }, [analytics, eventHandle, getShowIntercept, guestLinkUseRedirectService, props, safeLinksEnabled]);

    const onClose = useCallback(() => {
      setIsOpen(null);
      setUrl('');
    }, []);

    return (
      <>
        <WrappedComponent {...props} />
        {url && (
          <LinkConfirmationDialog
            href={url}
            isOpen={isOpen?.type === 'safeLinkConfirmation'}
            onClose={onClose}
          />
        )}
        {url && (
          <HotelSuggestionDialog
            href={url}
            isAllowListedUrl={!!isOpen?.isAllowListedUrl}
            isOpen={isOpen?.type === 'intercept'}
            onClose={onClose}
          />
        )}
      </>
    );
  };
};

export default wrapHtmlWithSafeLink;
